import { useState, useMemo, useContext } from 'react'
import { observer } from 'mobx-react'
import { Input, Tree } from 'antd'
import { MenuUnfoldOutlined, MenuFoldOutlined } from '@ant-design/icons'
import { EditTreeContext, LoadableButton, postMsg } from 'src/components'
import { $consts } from 'src/plugins'

const EditTreePageTree = () => {
  const tree = useContext(EditTreeContext)

  // 树展开收起
  const [collapsed, setCollapsed] = useState(false)

  // 树单选中
  const handleTreeNodesSelect = (selectedKeys: string[]) => {
    tree.setSelectedKeys(selectedKeys)
  }

  // 树复选中
  const handleTreeNodesCheck = ({ checked: selectedKeys }: any) => {
    const { Nav_PageTree = {} } = tree.treeConfig
    const { MULTI_CHECK_COUNT } = Nav_PageTree
    if (MULTI_CHECK_COUNT && selectedKeys.length > MULTI_CHECK_COUNT) {
      postMsg(`最多可选${MULTI_CHECK_COUNT}个节点`, 'error')
      return
    }
    handleTreeNodesSelect(selectedKeys)
  }

  // 子树数据
  const loadData = (node: any) => {
    if (node.TREE?.IS_LEAF) return Promise.resolve()

    return tree.getTreeData(node.TREE)
  }

  // 构造树数据
  const validTreeData = useMemo(() => {
    const renderTreeData = (treeData: any[]): any[] => {
      return treeData.map(item => {
        const { ID, NAME, IS_LEAF, __children__ } = item
        return {
          key: ID,
          title: NAME,
          children: renderTreeData(__children__ ?? []),
          isLeaf: IS_LEAF && !__children__?.length,
          TREE: item
        }
      })
    }

    return renderTreeData(tree.visibleTreeData)
  }, [tree.visibleTreeData])

  const { treeConfig, treeData, enableBtns, pageDomSize, selectedNodes, selectedNodesToolBtns } = tree
  const { Nav_PageTree = {} } = treeConfig
  const { LABEL_NAME, MULTI_CHECK, CAN_ADD_NODE } = Nav_PageTree

  if (!LABEL_NAME || !treeData.length || !pageDomSize.clientHeight) {
    if (!treeData.length && CAN_ADD_NODE) {
      const addBtns = enableBtns.filter((btn: any) => {
        return btn.BTN_TYPE === $consts['TABLE/BTN_TYPE_ADD'] || btn.IS_SAME_LEVEL
      })
      const sameLevelBtn = addBtns.find((btn: any) => btn.IS_SAME_LEVEL) ?? addBtns[0]
      if (sameLevelBtn) {
        return (
          <LoadableButton
            pageType={$consts['TABLE/FORM_TYPE_EDITTREE_PAGE']}
            locateBtn={$consts['TABLE/BTN_LOCATE_HEADER']}
            config={sameLevelBtn}
          />
        )
      }
      return <p>没有配置新增按钮</p>
    }

    return null
  }

  const treeHeight = pageDomSize.clientHeight - 24 - 24 - 34 - 28 - 40 - 8
  return (
    <div className={`tree-page__tree ${collapsed ? 'collapsed' : ''}`}>
      <div onClick={() => setCollapsed(!collapsed)} className='tree-page__tree-collapse'>
        {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
      </div>
      <div onClick={() => collapsed && setCollapsed(false)} className='tree-page__tree-wrap'>
        <div className='tree-page__tree-title'>{LABEL_NAME}</div>
        <div className='tree-page__tree-tools'>
          <div className='tree-page__tree-toolsTitle'>当前选中：{selectedNodes.length > 1 ? `${selectedNodes.length}个` : (selectedNodes[0]?.NAME || '暂无')}</div>
          <div className='tree-page__tree-toolsBtn'>
            {
              selectedNodesToolBtns.map((btn: any, index: number) => {
                return (
                  <LoadableButton
                    key={`${btn.ID}_${index}`}
                    pageType={$consts['TABLE/FORM_TYPE_EDITTREE_PAGE']}
                    locateBtn={$consts['TABLE/BTN_LOCATE_HEADER']}
                    config={btn}
                  />
                )
              })
            }
          </div>
        </div>
        <Input.Search
          allowClear
          onSearch={val => tree.setSearchKey(val)}
          placeholder='关键字搜索'
          className='tree-page__tree-search'
        />
        <div className='tree-page__tree-main'>
          <Tree
            checkStrictly
            height={treeHeight}
            checkable={MULTI_CHECK}
            checkedKeys={tree.selectedKeys}
            selectable={!MULTI_CHECK}
            selectedKeys={tree.selectedKeys}
            treeData={validTreeData}
            {...tree.defaultExpand}
            loadData={loadData}
            onSelect={handleTreeNodesSelect}
            onCheck={handleTreeNodesCheck}
          />
        </div>
      </div>
    </div>
  )
}

export default observer(EditTreePageTree)